import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, Music2, Guitar } from "lucide-react";

interface TransposeControlProps {
  transpose: number; // semitonos de transposición (+/-)
  capo: number; // traste de cejilla (0-12)
  originalKey?: string;
  onTransposeChange: (semitones: number) => void;
  onCapoChange: (fret: number) => void;
}

export const TransposeControl: React.FC<TransposeControlProps> = ({
  transpose,
  capo,
  originalKey,
  onTransposeChange,
  onCapoChange,
}) => {
  const currentKey = originalKey
    ? `${originalKey}${transpose > 0 ? " +" + transpose : transpose < 0 ? " " + transpose : ""}`
    : transpose !== 0
    ? `${transpose > 0 ? "+" : ""}${transpose} semitono${Math.abs(transpose) !== 1 ? "s" : ""}`
    : "Original";

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
            <Music2 className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Transposición</h3>
            <AnimatePresence mode="wait">
              <motion.p
                key={currentKey}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                className="text-xs text-slate-500"
              >
                {currentKey}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>
        {transpose !== 0 && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={() => onTransposeChange(0)}
            className="text-[10px] font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded-md transition-colors"
          >
            Reset
          </motion.button>
        )}
      </div>

      {/* Transpose stepper */}
      <div className="flex items-center gap-3">
        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={() => onTransposeChange(transpose - 1)}
          className="w-9 h-9 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors"
          aria-label="Bajar semitono"
        >
          <Minus className="w-4 h-4" />
        </motion.button>

        <div className="flex-1 relative h-9 bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
          <motion.div
            className="absolute top-0 bottom-0 bg-indigo-100"
            initial={false}
            animate={{
              left: "50%",
              width: `${Math.abs(transpose) * 8}%`,
              x: transpose >= 0 ? 0 : -Math.abs(transpose) * 8 + "%",
            }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.span
                key={transpose}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className={`text-sm font-bold ${transpose !== 0 ? "text-indigo-700" : "text-slate-400"}`}
              >
                {transpose > 0 ? `+${transpose}` : transpose}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>

        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={() => onTransposeChange(transpose + 1)}
          className="w-9 h-9 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors"
          aria-label="Subir semitono"
        >
          <Plus className="w-4 h-4" />
        </motion.button>
      </div>

      {/* Divider */}
      <div className="h-px bg-slate-100" />

      {/* Capo control */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <Guitar className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Capo</h3>
              <p className="text-xs text-slate-500">
                {capo > 0 ? `Cejilla traste ${capo}` : "Sin cejilla"}
              </p>
            </div>
          </div>
          {capo > 0 && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={() => onCapoChange(0)}
              className="text-[10px] font-medium text-amber-600 hover:text-amber-700 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded-md transition-colors"
            >
              Quitar
            </motion.button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400 w-4">0</span>
          <div className="flex-1 flex gap-1">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((fret) => (
              <motion.button
                key={fret}
                whileTap={{ scale: 0.9 }}
                onClick={() => onCapoChange(capo === fret ? 0 : fret)}
                className={`flex-1 h-7 rounded-md text-[10px] font-bold transition-colors ${
                  capo === fret
                    ? "bg-amber-500 text-white shadow-sm"
                    : "bg-slate-50 text-slate-400 hover:bg-slate-100 border border-slate-200"
                }`}
              >
                {fret}
              </motion.button>
            ))}
          </div>
          <span className="text-[10px] text-slate-400 w-4">12</span>
        </div>
      </div>
    </div>
  );
};

export default TransposeControl;
